import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ArtistLayout from '../components/layout/ArtistLayout';
import { 
  Rocket, 
  Plus, 
  Trash2, 
  Edit2,
  X,
  Target,
  BookOpen,
  Briefcase,
  Lightbulb,
  Calendar
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const CATEGORY_CONFIG = {
  career: { icon: Briefcase, label: 'Career Growth', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300' },
  learning: { icon: BookOpen, label: 'Learning', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' },
  personal: { icon: Target, label: 'Personal', color: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' },
  project: { icon: Lightbulb, label: 'Project Ideas', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300' }
};

const ArtistPlans = () => {
  const { isDark } = useTheme();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    category: 'career',
    description: '',
    target_date: '',
    progress_notes: ''
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get('/artist/plans');
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Please enter a plan title');
      return;
    }

    try {
      if (editingPlan) {
        await api.put(`/artist/plans/${editingPlan.id}`, formData);
        toast.success('Plan updated');
      } else {
        await api.post('/artist/plans', formData);
        toast.success('Plan created');
      }
      resetForm();
      fetchPlans();
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error('Failed to save plan');
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    
    try {
      await api.delete(`/artist/plans/${planId}`);
      toast.success('Plan deleted');
      fetchPlans();
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Failed to delete plan');
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      title: plan.title,
      category: plan.category,
      description: plan.description || '',
      target_date: plan.target_date || '',
      progress_notes: plan.progress_notes || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: 'career',
      description: '',
      target_date: '',
      progress_notes: ''
    });
    setEditingPlan(null);
    setShowForm(false);
  };

  const filteredPlans = plans.filter(plan => {
    if (filterCategory === 'all') return true;
    return plan.category === filterCategory;
  });

  const groupedPlans = filteredPlans.reduce((acc, plan) => {
    const cat = plan.category || 'personal';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(plan);
    return acc;
  }, {});

  return (
    <ArtistLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="artist-plans">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className={cn("text-2xl font-bold font-outfit flex items-center gap-2", isDark ? "text-white" : "text-slate-800")}>
              <Rocket className="w-7 h-7 text-[#331062]" />
              Future Plans
            </h1>
            <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
              Plan your career goals, learning objectives, and project ideas
            </p>
          </div>
          <Button 
            onClick={() => setShowForm(!showForm)}
            className="bg-[#331062] hover:bg-[#2A0D52]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Plan
          </Button>
        </div>

        {/* Category Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
            const count = plans.filter(p => p.category === key).length;
            const Icon = config.icon;
            return (
              <Card 
                key={key}
                className={cn(
                  "p-4 cursor-pointer transition-all hover:shadow-md",
                  filterCategory === key && "ring-2 ring-[#331062]",
                  isDark ? "bg-slate-800 border-slate-700" : ""
                )}
                onClick={() => setFilterCategory(filterCategory === key ? 'all' : key)}
              >
                <div className="flex items-center gap-3">
                  <div className={cn("p-2 rounded-lg", config.color)}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className={cn("text-xl font-bold", isDark ? "text-white" : "text-slate-800")}>{count}</p>
                    <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>{config.label}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Add/Edit Form */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Card className={cn("p-6", isDark ? "bg-slate-800 border-slate-700" : "")}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={cn("font-semibold", isDark ? "text-white" : "text-slate-800")}>
                    {editingPlan ? 'Edit Plan' : 'New Plan'}
                  </h3>
                  <button onClick={resetForm} className="text-slate-400 hover:text-slate-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="title">Plan Title *</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="e.g., Learn Advanced Animation"
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="career">💼 Career Growth</SelectItem>
                          <SelectItem value="learning">📚 Learning</SelectItem>
                          <SelectItem value="personal">🎯 Personal</SelectItem>
                          <SelectItem value="project">💡 Project Ideas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe your plan in detail..."
                      rows={3}
                      className={cn(
                        "w-full mt-1 px-3 py-2 rounded-md border text-sm resize-none",
                        isDark 
                          ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400" 
                          : "bg-white border-slate-300"
                      )}
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="target_date">Target Date</Label>
                      <Input
                        id="target_date"
                        type="date"
                        value={formData.target_date}
                        onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="progress_notes">Progress Notes</Label>
                      <Input
                        id="progress_notes"
                        value={formData.progress_notes}
                        onChange={(e) => setFormData({ ...formData, progress_notes: e.target.value })}
                        placeholder="Current progress..."
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1 bg-[#331062] hover:bg-[#2A0D52]">
                      {editingPlan ? 'Update Plan' : 'Create Plan'}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Plans Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
          </div>
        ) : filteredPlans.length === 0 ? (
          <Card className={cn("p-12 text-center", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <Rocket className={cn("w-16 h-16 mx-auto mb-4", isDark ? "text-slate-600" : "text-slate-300")} />
            <h3 className={cn("text-lg font-medium mb-2", isDark ? "text-white" : "text-slate-800")}>
              No plans yet
            </h3>
            <p className={cn("text-sm mb-4", isDark ? "text-slate-400" : "text-slate-500")}>
              Start planning your future by adding your first goal
            </p>
            <Button onClick={() => setShowForm(true)} className="bg-[#331062] hover:bg-[#2A0D52]">
              <Plus className="w-4 h-4 mr-2" /> Add Plan
            </Button>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedPlans).map(([category, categoryPlans]) => {
              const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.personal;
              const Icon = config.icon;
              
              return (
                <div key={category}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className={cn("w-5 h-5", isDark ? "text-slate-400" : "text-slate-500")} />
                    <h3 className={cn("font-semibold", isDark ? "text-white" : "text-slate-800")}>
                      {config.label}
                    </h3>
                    <span className={cn("text-xs px-2 py-0.5 rounded-full", config.color)}>
                      {categoryPlans.length}
                    </span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <AnimatePresence>
                      {categoryPlans.map((plan) => (
                        <motion.div
                          key={plan.id}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                        >
                          <Card className={cn(
                            "p-5 h-full",
                            isDark ? "bg-slate-800 border-slate-700" : ""
                          )}>
                            <div className="flex items-start justify-between mb-3">
                              <span className={cn("text-xs px-2 py-1 rounded-full", config.color)}>
                                {config.label}
                              </span>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEdit(plan)}
                                  className="h-7 w-7"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDelete(plan.id)}
                                  className="h-7 w-7 text-red-500 hover:text-red-600"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                            <h4 className={cn("font-semibold mb-2", isDark ? "text-white" : "text-slate-800")}>
                              {plan.title}
                            </h4>
                            {plan.description && (
                              <p className={cn("text-sm mb-3 line-clamp-3", isDark ? "text-slate-400" : "text-slate-500")}>
                                {plan.description}
                              </p>
                            )}
                            <div className="space-y-2">
                              {plan.target_date && (
                                <p className={cn("text-xs flex items-center gap-1", isDark ? "text-slate-500" : "text-slate-400")}>
                                  <Calendar className="w-3 h-3" />
                                  Target: {new Date(plan.target_date).toLocaleDateString()}
                                </p>
                              )}
                              {plan.progress_notes && (
                                <div className={cn(
                                  "text-xs p-2 rounded-md",
                                  isDark ? "bg-slate-700" : "bg-slate-50"
                                )}>
                                  <span className="font-medium">Progress:</span> {plan.progress_notes}
                                </div>
                              )}
                            </div>
                          </Card>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ArtistLayout>
  );
};

export default ArtistPlans;
